import PropTypes from 'prop-types';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import getPositionStyles from '@activebrands/core-web/utils/style-functions/get-position-styles';
import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';

// Decide fontKeys based on the blocks context
const fontKeys = {
    fullWidth: {
        heading: ['Primary/48_100_-3', null, null, 'Primary/72_100_-3', null, 'Primary/96_100_-3'],
        paragraph: ['Secondary/16_130'],
        preamble: ['Primary/24_120_-2', null, null, 'Primary/32_110_-2'],
        subHeading: ['Primary/32_110_-2', null, null, 'Primary/48_100_-3', null, 'Primary/60_100_-3'],
        testimonial: ['Primary/24_120_-2', null, null, 'Primary/40_100_-2'],
        quote: {
            text: ['Primary/24_120_-2', null, null, 'Primary/40_100_-2'],
            author: ['Miscellaneous/16_100_-1'],
        },
    },
    oneColumn: {
        heading: ['Primary/48_100_-3', null, null, 'Primary/72_100_-3', null, 'Primary/96_100_-3'],
        paragraph: ['Secondary/16_130'],
        preamble: ['Primary/24_120_-2', null, null, 'Primary/32_110_-2'],
        subHeading: ['Primary/32_110_-2', null, null, 'Primary/48_100_-3', null, 'Primary/60_100_-3'],
        testimonial: ['Primary/24_120_-2', null, null, 'Primary/40_100_-2'],
        quote: {
            text: ['Primary/24_120_-2', null, null, 'Primary/40_100_-2'],
            author: ['Miscellaneous/16_100_-1'],
        },
    },
    twoColumns: {
        heading: ['Primary/32_110_-2', null, null, 'Primary/48_100_-3', null, 'Primary/60_100_-3'],
        paragraph: ['Secondary/14_130', null, null, 'Secondary/16_130'],
        preamble: ['Primary/18_120_-1', null, null, 'Primary/24_120_-2', null, 'Primary/32_110_-2'],
        subHeading: ['Primary/24_120_-2', null, null, 'Primary/32_110_-2', null, 'Primary/48_100_-3'],
        testimonial: ['Primary/24_120_-2', null, null, 'Primary/40_100_-2'],
        quote: {
            text: ['Primary/16_120_-1', null, null, 'Primary/24_120_-2'],
            author: ['Miscellaneous/16_100_-1'],
        },
    },
    threeColumns: {
        heading: ['Primary/32_110_-2', null, null, 'Primary/48_100_-3', null, 'Primary/60_100_-3'],
        paragraph: ['Secondary/14_130', null, null, 'Secondary/16_130'],
        preamble: ['Primary/18_120_-1', null, null, 'Primary/24_120_-2', null, 'Primary/32_110_-2'],
        subHeading: ['Primary/24_120_-2', null, null, 'Primary/32_110_-2', null, 'Primary/48_100_-3'],
        testimonial: ['Primary/24_120_-2', null, null, 'Primary/40_100_-2'],
        quote: {
            text: ['Primary/16_120_-1', null, null, 'Primary/24_120_-2'],
            author: ['Miscellaneous/16_100_-1'],
        },
    },
};

const Text = ({ contentHorizontalAlignment = 'left', id = '', items = [], layout = 'oneColumn' }) => {
    const [css] = useStyletron();

    if (!items?.length) {
        return null;
    }

    const textComponentFontKeys = fontKeys[layout];
    const textComponentStyling = { marginBottom: ['32px', null, '24px'] };

    const positionStyles = getPositionStyles({
        contentHorizontalAlignment,
        contentVerticalAlignment: 'top',
    });

    return (
        <div className={css({ display: 'flex', ...positionStyles })} id={id}>
            <div className={css({ width: layout === 'fullWidth' ? '100%' : ['100%', null, null, null, null, '50%'] })}>
                {items.map(item => {
                    switch (item.component) {
                        case 'heading':
                            return (
                                <Heading
                                    key={item.id}
                                    $style={textComponentStyling}
                                    as={item.type || "h2"}
                                    fontKeys={textComponentFontKeys.heading}
                                    {...item.sbEditable}
                                >
                                    {item.text}
                                </Heading>
                            );
                        case 'paragraph':
                            return (
                                <div
                                    key={item.id}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.paragraph),
                                        ...textComponentStyling,
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'preamble':
                            return (
                                <div
                                    key={item.id}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.preamble),
                                        ...textComponentStyling,
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'subHeading':
                            return (
                                <Heading
                                    key={item.id}
                                    $style={textComponentStyling}
                                    as={item.type || "h3"}
                                    fontKeys={textComponentFontKeys.subHeading}
                                    {...item.sbEditable}
                                >
                                    {item.text}
                                </Heading>
                            );
                        case 'testimonial':
                            return (
                                <div
                                    key={item.id}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.testimonial),
                                        ...textComponentStyling,
                                        padding: '40px 0',
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                </div>
                            );
                        case 'quote':
                            return (
                                <div
                                    key={item.id}
                                    className={css({
                                        ...responsiveTextStyles(textComponentFontKeys.quote.text),
                                        ...textComponentStyling,
                                        padding: '40px 0',
                                    })}
                                    {...item.sbEditable}
                                >
                                    <RichText data={item.text} />
                                    <Paragraph
                                        $style={{ marginTop: '24px' }}
                                        fontKeys={textComponentFontKeys.quote.author}
                                    >
                                        — {item.author}
                                    </Paragraph>
                                </div>
                            );

                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
};
Text.propTypes = {
    contentHorizontalAlignment: PropTypes.string,
    id: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object),
    layout: PropTypes.oneOf(Object.keys(fontKeys)),
};

export default Text;
